import { useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, Shuffle, Repeat, Repeat1, Music2 } from 'lucide-react';
import { useAudio } from '../../context/AudioContext';
import { useGlobalContext } from '../../context/GlobalContext';

const NowPlaying = () => {
  const [imageError, setImageError] = useState({});
  const { darkMode } = useGlobalContext();
  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    volume,
    handleVolumeChange,
    currentTime,
    duration,
    formatTime,
    handleSeek,
    handleNextTrack,
    handlePreviousTrack,
    shuffle,
    toggleShuffle,
    repeatMode,
    toggleRepeatMode,
  } = useAudio();

  const sliderFill = darkMode ? '#836925' : '#7A2218';
  const sliderTrack = darkMode ? '#2E2C2A' : '#E4E1DB';
  const seekPercent = duration ? (currentTime / duration) * 100 : 0;

  const primaryImage = currentTrack?.albumArt || currentTrack?.artistImage;
  const showSecondaryArtist = !!currentTrack?.albumArt && !!currentTrack?.artistImage;

  const toggleBtnClass = (active) =>
    `p-2 rounded-full transition-colors ${
      active
        ? 'bg-lz-ember/15 text-lz-ember hover:bg-lz-ember/25 dark:bg-lz-candle-dim/25 dark:text-lz-candle-bright dark:hover:bg-lz-candle-dim/35'
        : 'text-lz-ink-mid hover:text-black hover:bg-lz-concrete dark:text-lz-concrete dark:hover:text-white dark:hover:bg-lz-ink-mid/60'
    }`;

  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;
  const repeatLabel = `Repeat ${repeatMode === 'off' ? 'off' : repeatMode}`;

  return (
    <div className="flex flex-col items-center h-full">
      <h2 className="text-2xl font-bold text-lz-ink-mid dark:text-lz-concrete mb-8 self-start">Music</h2>

      <div className="flex-1 flex flex-col items-center justify-center w-full min-h-0">
        <div className="w-full max-w-[240px] aspect-square rounded-2xl overflow-hidden shadow-xl dark:shadow-2xl mb-4 relative bg-lz-concrete dark:bg-lz-ink-mid">
          {primaryImage && !imageError[currentTrack.id] ? (
            <img
              src={primaryImage}
              alt={`${currentTrack.trackName} cover`}
              className="absolute inset-0 w-full h-full object-cover"
              crossOrigin="anonymous"
              onError={() => setImageError(prev => ({ ...prev, [currentTrack.id]: true }))}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Music2 className="w-40 h-40 text-lz-ink-mid dark:text-lz-candle-dim" />
            </div>
          )}
        </div>

        {showSecondaryArtist && (
          <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg mb-3 relative bg-lz-concrete dark:bg-lz-ink-mid flex-shrink-0">
            {!imageError[`artist-${currentTrack.id}`] ? (
              <img
                src={currentTrack.artistImage}
                alt={currentTrack.artist}
                className="absolute inset-0 w-full h-full object-cover"
                crossOrigin="anonymous"
                onError={() => setImageError(prev => ({ ...prev, [`artist-${currentTrack.id}`]: true }))}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Music2 className="w-8 h-8 text-lz-ink-mid dark:text-lz-candle-dim" />
              </div>
            )}
          </div>
        )}

        <div className="w-full max-w-sm">
          <div className="text-center mb-4">
            <div className="font-medium text-base md:text-lg text-black dark:text-lz-concrete">
              {currentTrack?.trackName}
            </div>
            <div className="text-sm text-lz-ink-mid dark:text-lz-concrete mt-1">
              {currentTrack?.artist} • {currentTrack?.album}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={currentTime}
                onChange={(e) => handleSeek(parseFloat(e.target.value))}
                aria-label="Track progress"
                aria-valuetext={duration ? `${formatTime(currentTime)} of ${formatTime(duration)}` : undefined}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-lz-ember dark:[&::-webkit-slider-thumb]:bg-lz-candle-dim [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                style={{
                  background: `linear-gradient(90deg, ${sliderFill} ${seekPercent}%, ${sliderTrack} ${seekPercent}%)`,
                }}
              />
              <div className="flex justify-between text-xs text-lz-ink-mid dark:text-lz-candle-bright">
                <span>{formatTime(currentTime)}</span>
                <span>{duration ? formatTime(duration) : '--:--'}</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-6">
              <button
                onClick={toggleShuffle}
                aria-pressed={shuffle}
                aria-label="Shuffle"
                className={toggleBtnClass(shuffle)}
              >
                <Shuffle size={20} />
              </button>
              <button
                onClick={handlePreviousTrack}
                aria-label="Previous track"
                className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
              >
                <SkipBack size={22} />
              </button>
              <button
                onClick={togglePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className="p-4 bg-lz-ember hover:bg-lz-ink-mid dark:bg-lz-candle-dim dark:hover:bg-lz-ink-mid text-white rounded-full transition-colors"
              >
                <span className="flex items-center justify-center w-7 h-7">
                  {isPlaying ? <Pause size={28} /> : <Play size={28} />}
                </span>
              </button>
              <button
                onClick={handleNextTrack}
                aria-label="Next track"
                className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
              >
                <SkipForward size={22} />
              </button>
              <button
                onClick={toggleRepeatMode}
                aria-label={repeatLabel}
                className={toggleBtnClass(repeatMode !== 'off')}
              >
                <RepeatIcon size={20} />
              </button>
            </div>

            <div className="flex items-center justify-center gap-3">
              <Volume2 size={20} className="text-lz-ink-mid dark:text-lz-candle-dim" />
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                aria-label="Volume"
                aria-valuetext={`${Math.round(volume)} percent`}
                className="w-32 h-1.5 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:bg-lz-ember dark:[&::-webkit-slider-thumb]:bg-lz-candle-dim [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                style={{
                  background: `linear-gradient(90deg, ${sliderFill} ${volume}%, ${sliderTrack} ${volume}%)`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowPlaying;
